import { createEffect, createMemo, createSignal, For, onCleanup, onMount, Show, untrack } from 'solid-js';
import { createStore, produce } from 'solid-js/store';
import { useNavigate, useSearchParams } from '@solidjs/router';
import Text from '../components/Text';
import CraftPlanView from '../components/CraftPlanView';
import ItemIdentifierPicker from '../components/ItemIdentifierPicker';
import QuantityInput from '../components/QuantityInput';
import StorageSelect from '../components/StorageSelect';
import { EmptyState } from '../components/EmptyState';
import { useToast } from '../components/MessageBox';
import { useTranslation } from '../hooks/useTranslation';
import { useItemStacks } from '../hooks/useItemStacks';
import { useCraftingResolver } from '../services/craftingResolverContext';
import { parseItemKey } from '../services/craftingResolver';
import { useInventoryApi, type CraftOrderPlan, type CraftOrderPlanLine, type CustomItemDefinition, type StorageInfo } from '../services/inventoryApi';
import { formatDuration } from '../services/durationFormat';
import styles from './InventoryCraftOrder.module.css';

const PLAN_DEBOUNCE_MS = 350;

interface OrderLine {
    id: number;
    itemKey: string;
    quantity: number;
}

let nextLineId = 1;
const emptyLine = (): OrderLine => ({ id: nextLineId++, itemKey: '', quantity: 1 });

export default function InventoryCraftOrder() {
    const { t } = useTranslation();
    const toast = useToast();
    const navigate = useNavigate();
    const inv = useInventoryApi();
    const resolver = useCraftingResolver();
    const [searchParams] = useSearchParams();

    const [storages, setStorages] = createSignal<StorageInfo[]>([]);
    const [loading, setLoading] = createSignal(true);
    const [sourceStorageId, setSourceStorageId] = createSignal('');
    const [resultStorageId, setResultStorageId] = createSignal('');
    // A store, not a signal of arrays: QuantityInput reports its value from an effect, so replacing the
    // line object would make For rebuild the row, which fires that effect again and never settles.
    const [lines, setLines] = createStore<OrderLine[]>([emptyLine()]);
    const [expanded, setExpanded] = createSignal<number[]>([]);

    const [plan, setPlan] = createSignal<CraftOrderPlan | null>(null);
    const [planLoading, setPlanLoading] = createSignal(false);
    const [submitting, setSubmitting] = createSignal(false);

    const [customItems, setCustomItems] = createSignal<CustomItemDefinition[]>([]);
    const { customStackFor } = useItemStacks(customItems);

    const identifierFor = (key: string) => {
        const def = resolver()?.getAllItems()?.find(i => i.key === key);
        return def ? `minecraft:${def.name}:${def.metadata}` : null;
    };

    const keyForIdentifier = (identifier: string) => {
        const bare = identifier.replace(/^minecraft:/, '');
        const [name, metaText] = bare.split(':');
        const items = resolver()?.getAllItems() ?? [];
        const def = metaText !== undefined
            ? items.find(i => i.name === name && i.metadata === Number(metaText))
            : items.find(i => i.name === name);
        return def?.key ?? null;
    };

    const pickerValue = (key: string) => {
        if (!key) return [];
        if (parseItemKey(key)?.hash) return [key];
        const identifier = identifierFor(key);
        return identifier ? [identifier] : [];
    };

    const filledLines = createMemo(() => lines.filter(line => line.itemKey && line.quantity > 0));

    // The backend keys its plan by position among the lines it was sent, which is the filled ones.
    const planLineFor = (line: OrderLine): CraftOrderPlanLine | undefined => {
        const position = filledLines().findIndex(candidate => candidate.id === line.id);
        return position < 0 ? undefined : plan()?.lines[position];
    };

    const lineBlocked = (line: OrderLine) => {
        const linePlan = planLineFor(line);
        return !!linePlan && (!!linePlan.planError || linePlan.shortfalls.length > 0);
    };

    const updateLine = (id: number, patch: Partial<OrderLine>) =>
        setLines(line => line.id === id, patch);

    const addLine = () => {
        const line = emptyLine();
        setLines(produce(current => { current.push(line); }));
        setExpanded(current => [...current, line.id]);
    };

    // The last row is emptied rather than removed: a form with no rows at all has nothing to type into.
    const removeLine = (id: number) => {
        setLines(produce(current => {
            if (current.length === 1) { current[0] = emptyLine(); return; }
            const position = current.findIndex(line => line.id === id);
            if (position >= 0) current.splice(position, 1);
        }));
        setExpanded(current => current.filter(lineId => lineId !== id));
    };

    const toggleExpanded = (id: number) =>
        setExpanded(current => (current.includes(id) ? current.filter(lineId => lineId !== id) : [...current, id]));

    let planTimer: number | undefined;
    createEffect(() => {
        const source = sourceStorageId();
        const result = resultStorageId();
        const payload = filledLines().map(line => ({ itemKey: line.itemKey, quantity: line.quantity }));

        if (!source || !result || payload.length === 0) { setPlan(null); return; }

        untrack(() => {
            window.clearTimeout(planTimer);
            planTimer = window.setTimeout(() => { void loadPlan(source, result, payload); }, PLAN_DEBOUNCE_MS);
        });
    });

    onCleanup(() => window.clearTimeout(planTimer));

    const loadPlan = async (source: string, result: string, payload: { itemKey: string; quantity: number }[]) => {
        setPlanLoading(true);
        try {
            setPlan(await inv.planCraftOrder({ sourceStorageId: source, resultStorageId: result, lines: payload }));
        } catch {
            setPlan(null);
            toast?.error(t('INVENTORY_CRAFT_PLAN_FAILED'));
        } finally {
            setPlanLoading(false);
        }
    };

    const blockingError = createMemo(() => {
        const current = plan();
        if (!current) return null;
        if (current.lines.some(line => line.planError || line.shortfalls.length > 0))
            return t('INVENTORY_CRAFT_ORDER_BLOCKED_HINT');
        if (current.eligibleBotIds.length === 0) return t('INVENTORY_CRAFT_NO_SHARED_BOT');
        return current.botError ?? null;
    });

    const submitDisabled = () =>
        submitting() || planLoading() || filledLines().length === 0
        || !sourceStorageId() || !resultStorageId() || !plan() || blockingError() !== null;

    const submit = async () => {
        setSubmitting(true);
        try {
            await inv.createCraftOrder({
                sourceStorageId: sourceStorageId(),
                resultStorageId: resultStorageId(),
                lines: filledLines().map(line => ({ itemKey: line.itemKey, quantity: line.quantity })),
            });
            toast?.success(t('INVENTORY_CRAFT_ORDER_QUEUED'));
            navigate('/inventory');
        } catch (err) {
            toast?.error(err instanceof Error ? err.message : t('INVENTORY_CRAFT_ORDER_SUBMIT_FAILED'));
        } finally {
            setSubmitting(false);
        }
    };

    onMount(async () => {
        inv.fetchCustomItems().then(setCustomItems).catch(() => {});
        try {
            const list = await inv.fetchStorages();
            setStorages(list);
            const presetSource = (searchParams.source as string | undefined) ?? list[0]?.id ?? '';
            setSourceStorageId(presetSource);
            setResultStorageId(presetSource);
        } catch {
            toast?.error(t('INVENTORY_CRAFT_STORAGES_FAILED'));
        } finally {
            setLoading(false);
        }
    });

    return (
        <div class={styles.container}>
            <div class={styles.headerRow}>
                <h1><Text val="INVENTORY_CRAFT_ORDER_TITLE" /></h1>
            </div>
            <p class={styles.subtitle}><Text val="INVENTORY_CRAFT_ORDER_SUBTITLE" /></p>

            <Show
                when={!loading()}
                fallback={<div class={styles.centerWait}><i class="fa-solid fa-spinner fa-spin fa-2x" /></div>}
            >
                <Show
                    when={storages().length > 0}
                    fallback={<EmptyState
                        icon="fa-solid fa-boxes-stacked"
                        title={t('INVENTORY_CRAFT_NO_STORAGE_TITLE')}
                        hint={t('INVENTORY_CRAFT_NO_STORAGE_HINT')}
                    />}
                >
                    <form class="fcard v" style={{ gap: 'var(--spacing-lg)' }} onSubmit={(e) => { e.preventDefault(); void submit(); }}>
                        <div class={styles.storageRow}>
                            <label class="v" style={{ gap: 'var(--spacing-xs)' }}>
                                <span class={styles.fieldLabel}><Text val="INVENTORY_CRAFT_SOURCE" /></span>
                                <StorageSelect
                                    storages={storages()}
                                    value={sourceStorageId()}
                                    onChange={(id) => {
                                        const wasMirrored = resultStorageId() === sourceStorageId();
                                        setSourceStorageId(id);
                                        if (wasMirrored) setResultStorageId(id);
                                    }}
                                />
                            </label>
                            <label class="v" style={{ gap: 'var(--spacing-xs)' }}>
                                <span class={styles.fieldLabel}><Text val="INVENTORY_CRAFT_RESULT" /></span>
                                <StorageSelect storages={storages()} value={resultStorageId()} onChange={setResultStorageId} />
                            </label>
                        </div>

                        <div class="v" style={{ gap: 'var(--spacing-sm)' }}>
                            <span class={styles.fieldLabel}><Text val="INVENTORY_CRAFT_ORDER_LINES" /></span>

                            <For each={lines}>{(line, index) => (
                                <div classList={{ [styles.lineCard]: true, [styles.lineCardBlocked]: lineBlocked(line) }}>
                                    <div class={styles.lineHead}>
                                        <span class={styles.lineIndex}>{index() + 1}</span>
                                        <div class={styles.linePicker}>
                                            <ItemIdentifierPicker
                                                single
                                                metaAware
                                                value={pickerValue(line.itemKey)}
                                                stackForValue={customStackFor}
                                                onChange={(next) => updateLine(line.id, { itemKey: next[0] ? keyForIdentifier(next[0]) ?? '' : '' })}
                                                filter={(def) => resolver()?.hasRecipeForKey(def.key) ?? false}
                                                placeholder={t('INVENTORY_CRAFT_ORDER_LINE_PICK')}
                                                label={t('INVENTORY_CRAFT_ORDER_LINE_ITEM')}
                                            />
                                        </div>
                                        <div class={styles.lineQuantity}>
                                            <QuantityInput
                                                value={line.quantity}
                                                min={1}
                                                stackSize={resolver()?.getItem(line.itemKey)?.stackSize}
                                                onChange={(next) => updateLine(line.id, { quantity: next })}
                                            />
                                        </div>

                                        <div class={styles.lineStatus}>
                                            <Show when={planLineFor(line)}>{(linePlan) => (
                                                <>
                                                    <span classList={{
                                                        [styles.lineBadge]: true,
                                                        [styles.lineBadgeReady]: !lineBlocked(line),
                                                        [styles.lineBadgeBlocked]: lineBlocked(line),
                                                    }}>
                                                        <Text val={lineBlocked(line) ? 'INVENTORY_CRAFT_ORDER_LINE_BLOCKED' : 'INVENTORY_CRAFT_ORDER_LINE_READY'} />
                                                    </span>
                                                    <Show when={formatDuration(linePlan().estimatedSeconds)}>{(lineEta) => (
                                                        <span class={styles.lineEta}>{t('INVENTORY_CRAFT_STEP_ETA', lineEta())}</span>
                                                    )}</Show>
                                                    <button
                                                        type="button"
                                                        class="btn btn-secondary btn-sm"
                                                        onClick={() => toggleExpanded(line.id)}
                                                    >
                                                        <Text val={expanded().includes(line.id) ? 'INVENTORY_CRAFT_ORDER_HIDE_PLAN' : 'INVENTORY_CRAFT_ORDER_SHOW_PLAN'} />
                                                    </button>
                                                </>
                                            )}</Show>
                                            <button
                                                type="button"
                                                class="btn btn-danger btn-icon btn-sm"
                                                title={t('INVENTORY_CRAFT_ORDER_REMOVE_LINE')}
                                                onClick={() => removeLine(line.id)}
                                            >
                                                <i class="fa-solid fa-trash" />
                                            </button>
                                        </div>
                                    </div>

                                    <Show when={!line.itemKey}>
                                        <p class={styles.lineHint}><Text val="INVENTORY_CRAFT_ORDER_LINE_EMPTY" /></p>
                                    </Show>

                                    <Show when={expanded().includes(line.id) && planLineFor(line)}>{(linePlan) => (
                                        <div class={styles.linePlan}>
                                            <CraftPlanView
                                                steps={linePlan().steps}
                                                shortfalls={linePlan().shortfalls}
                                                totalConsumed={linePlan().totalConsumed}
                                                estimatedSeconds={linePlan().estimatedSeconds}
                                                planError={linePlan().planError}
                                                customItems={customItems()}
                                                hideStepsHeader
                                            />
                                        </div>
                                    )}</Show>
                                </div>
                            )}</For>

                            <div class="h">
                                <button type="button" class="btn btn-secondary btn-sm" onClick={addLine}>
                                    <i class="fa-solid fa-plus" /> <Text val="INVENTORY_CRAFT_ORDER_ADD_LINE" />
                                </button>
                            </div>
                        </div>

                        <Show when={blockingError()}>
                            <p class={styles.blocking}><i class="fa-solid fa-triangle-exclamation" /> {blockingError()}</p>
                        </Show>

                        <div class="h" style={{ 'justify-content': 'flex-end', gap: 'var(--spacing-sm)' }}>
                            <button type="button" class="btn btn-secondary" onClick={() => navigate('/inventory')}>
                                <Text val="CANCEL" />
                            </button>
                            <button type="submit" class="btn btn-primary" disabled={submitDisabled()}>
                                <Show when={submitting()} fallback={<><i class="fa-solid fa-hammer" /> <Text val="INVENTORY_CRAFT_ORDER_SUBMIT" /></>}>
                                    <i class="fa-solid fa-spinner fa-spin" />
                                </Show>
                            </button>
                        </div>
                    </form>

                    <div class="fcard v" style={{ gap: 'var(--spacing-md)' }}>
                        <div class={styles.totalRow}>
                            <h2 style={{ margin: 0 }}><Text val="INVENTORY_CRAFT_ORDER_TOTAL" /></h2>
                            <Show when={formatDuration(plan()?.estimatedSeconds ?? 0)}>{(orderEta) => (
                                <span class={styles.totalEta}>{t('INVENTORY_CRAFT_PLAN_ETA', orderEta())}</span>
                            )}</Show>
                        </div>

                        <Show
                            when={!planLoading()}
                            fallback={<div class={styles.centerWait}><i class="fa-solid fa-spinner fa-spin fa-2x" /></div>}
                        >
                            <Show
                                when={plan()}
                                fallback={<p class={styles.subtitle}><Text val="INVENTORY_CRAFT_PLAN_HINT" /></p>}
                            >
                                {(current) => (
                                    <CraftPlanView
                                        steps={[]}
                                        shortfalls={[]}
                                        totalConsumed={current().totalConsumed}
                                        estimatedSeconds={current().estimatedSeconds}
                                        customItems={customItems()}
                                    />
                                )}
                            </Show>
                        </Show>
                    </div>
                </Show>
            </Show>
        </div>
    );
}
